/* Content → Paid content → Pricing. The simple inputs (per-item prices, one
   negotiation floor, standard bundles) compile down to the full pricing matrix;
   the matrix itself stays editable under "Advanced pricing". Edits go into the
   content-policy draft, which the page autosaves. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, ContentPriceOverride, ContentPricingRules, PricePair, ShapeContentOverride, SimplePricingInputs, VideoTierKey } from '../../../../api/client';
import {
  MINUTE_BUCKETS, PHOTO_TIERS, PHOTO_TIER_LABEL, PRESET_KEYS, VIDEO_CATEGORIES, VIDEO_CATEGORY_LABEL, VIDEO_TIER_CATEGORIES, VIDEO_TIER_KEYS,
  categoryHasPricing, compileSimpleToRules, photoCell, presetPricingRules, readSimpleInputs, readStandardBundles, setPhotoCell, setVideoCell, videoCell,
  type PresetKey, type VideoCategory,
} from '../../ContentRequests/pricing';
import { AdvancedToggle, Btn, Card, CellLabel, Empty, Foot, Icon, NumInput, Row, Section, Seg, Select, Spin, Stack, THead, TRow, XButton } from '../kit';
import { font } from '../style';
import { C } from '../tokens';
import { rangeText } from './shapes';

const ITEM_COLS = 'minmax(0,1fr) 80px 80px';
const STD_COLS = 'minmax(0,1fr) 80px 80px 26px';

export function PricingTab({ rules, onRules, noVaults }: { rules: ContentPricingRules; onRules: (r: ContentPricingRules) => void; noVaults?: boolean }) {
  const { t } = useTranslation('assistantCompact');
  const [adv, setAdv] = useState(false);
  const [openShape, setOpenShape] = useState<number | null>(null);
  const simple = readSimpleInputs(rules);
  const bundles = readStandardBundles(rules);
  const commit = (s: SimplePricingInputs, b: ShapeContentOverride[]) => onRules(compileSimpleToRules(s, b, rules));

  const setTier = (tier: VideoTierKey, i: 0 | 1, v: number | null) => {
    const cur = simple.video[tier] ?? [null, null];
    commit({ ...simple, video: { ...simple.video, [tier]: i === 0 ? [v, cur[1]] : [cur[0], v] } }, bundles);
  };
  const patchStd = (i: number, p: Partial<ShapeContentOverride>) => commit(simple, bundles.map((b, k) => (k === i ? { ...b, ...p } : b)));
  const setStdPrice = (i: number, idx: 0 | 1, v: number | null) => {
    const cur = bundles[i]?.price ?? [null, null];
    patchStd(i, { price: (idx === 0 ? [v, cur[1]] : [cur[0], v]) as PricePair });
  };
  const setDim = (i: number, media: 'photos' | 'videos', which: 0 | 1, v: number | null) => {
    const shape = bundles[i]!.shape;
    const d = shape[media];
    const mn = which === 0 ? v : d?.[0] ?? null;
    const mx = which === 1 ? v : d?.[1] ?? null;
    const next: [number, number] | null = mn == null && mx == null ? null : [Math.max(0, mn ?? mx ?? 0), Math.max(mn ?? mx ?? 0, mx ?? mn ?? 0)];
    patchStd(i, { shape: { ...shape, [media]: next } });
  };
  const shapeLabel = (b: ShapeContentOverride) => {
    const parts: string[] = [];
    if (b.shape.photos) parts.push(t('pricing.photosN', { range: rangeText(b.shape.photos) }));
    if (b.shape.videos) parts.push(t('pricing.videosN', { range: rangeText(b.shape.videos) }));
    return parts.join(' + ') || t('pricing.emptyShape');
  };
  const floor = simple.floor_pct;

  return (
    <Stack>
      {noVaults && <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: font(500, 11.5), color: C.purplePale }}><Icon name="alert-circle" size={14} />{t('pricing.noVaults')}</span>}
      <Section title={t('pricing.perItem')} hint={t('pricing.perItemHint')}>
        <Card>
          <THead cols={ITEM_COLS}><span>{t('pricing.item')}</span><span>{t('pricing.short')}</span><span>{t('pricing.long')}</span></THead>
          <TRow cols={ITEM_COLS}>
            <CellLabel sub={t('pricing.flat')}>{t('pricing.photo')}</CellLabel>
            <NumInput value={simple.photo_price} onChange={(v) => commit({ ...simple, photo_price: v }, bundles)} min={0} max={9999} allowEmpty width="100%" />
            <NumInput value={null} onChange={() => {}} disabled placeholder="—" width="100%" />
          </TRow>
          {VIDEO_TIER_KEYS.map((tier) => (
            <TRow key={tier} cols={ITEM_COLS}>
              <CellLabel sub={VIDEO_TIER_CATEGORIES[tier].map((c) => VIDEO_CATEGORY_LABEL[c]).join(' · ')}>{t(`pricing.tier.${tier}`)}</CellLabel>
              <NumInput value={simple.video[tier]?.[0] ?? null} onChange={(v) => setTier(tier, 0, v)} min={0} max={9999} allowEmpty width="100%" />
              <NumInput value={simple.video[tier]?.[1] ?? null} onChange={(v) => setTier(tier, 1, v)} min={0} max={9999} allowEmpty width="100%" />
            </TRow>
          ))}
          <Row label={t('pricing.floorLabel')} help={t('pricing.floorHelp')}>
            <input
              type="range" min={0} max={100} step={5} value={floor} className="cpt-range"
              onChange={(e) => commit({ ...simple, floor_pct: +e.target.value }, bundles)}
              style={{ width: 200, background: `linear-gradient(to right,${C.purple} ${floor}%,${C.borderHi} ${floor}%)` }}
            />
            <span style={{ font: font(600, 12.5), color: C.purplePale, minWidth: 88, textAlign: 'right' }}>{t('pricing.ofAsking', { pct: floor })}</span>
          </Row>
        </Card>
      </Section>

      <Section
        title={t('pricing.std')}
        hint={t('pricing.stdHint')}
        right={<Btn kind="tint" onClick={() => { commit(simple, [...bundles, { shape: { photos: [5, 9], videos: null }, price: [null, null] }]); setOpenShape(bundles.length); }}>{t('pricing.addStd')}</Btn>}
      >
        <Card>
          <THead cols={STD_COLS}><span>{t('pricing.bundle')}</span><span>{t('pricing.asking')}</span><span>{t('pricing.floor')}</span><span /></THead>
          {bundles.map((b, i) => (
            <div key={i}>
              <TRow cols={STD_COLS}>
                <span style={{ font: font(500, 13), color: C.text }}>{shapeLabel(b)}</span>
                <NumInput value={b.price?.[0] ?? null} onChange={(v) => setStdPrice(i, 0, v)} min={0} max={9999} allowEmpty width="100%" />
                <NumInput value={b.price?.[1] ?? null} onChange={(v) => setStdPrice(i, 1, v)} min={0} max={9999} allowEmpty width="100%" />
                <button type="button" title={t('pricing.editShape')} onClick={() => setOpenShape(openShape === i ? null : i)} style={{ width: 26, height: 26, background: 'transparent', border: 0, color: openShape === i ? C.purpleLt : C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="sliders" size={14} />
                </button>
              </TRow>
              {openShape === i && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 14px 10px', font: font(400, 12), color: C.muted }}>
                  <span>{t('pricing.photos')}</span>
                  <NumInput value={b.shape.photos?.[0] ?? null} onChange={(v) => setDim(i, 'photos', 0, v)} min={0} max={99} int allowEmpty width={48} placeholder="0" />
                  <span>–</span>
                  <NumInput value={b.shape.photos?.[1] ?? null} onChange={(v) => setDim(i, 'photos', 1, v)} min={0} max={99} int allowEmpty width={48} placeholder="0" />
                  <span style={{ marginLeft: 8 }}>{t('pricing.videos')}</span>
                  <NumInput value={b.shape.videos?.[0] ?? null} onChange={(v) => setDim(i, 'videos', 0, v)} min={0} max={99} int allowEmpty width={48} placeholder="0" />
                  <span>–</span>
                  <NumInput value={b.shape.videos?.[1] ?? null} onChange={(v) => setDim(i, 'videos', 1, v)} min={0} max={99} int allowEmpty width={48} placeholder="0" />
                  <span style={{ flex: 1 }}>{t('pricing.shapeHint')}</span>
                  <Btn kind="text" height={24} onClick={() => { commit(simple, bundles.filter((_, k) => k !== i)); setOpenShape(null); }} style={{ color: C.sub }}>{t('pricing.removeStd')}</Btn>
                </div>
              )}
            </div>
          ))}
          {!bundles.length && <Empty>{t('pricing.noStd')}</Empty>}
        </Card>
      </Section>

      <AdvancedToggle open={adv} onToggle={() => setAdv((a) => !a)} label={t('pricing.advanced')} hint={t('pricing.advancedHint')} />
      {adv && <AdvancedPricing rules={rules} onRules={onRules} />}
    </Stack>
  );
}

function matrixHasPricing(r: ContentPricingRules | null | undefined) {
  return !!r && (Object.keys(r.photo ?? {}).length > 0 || Object.keys(r.video ?? {}).length > 0);
}

function AdvancedPricing({ rules, onRules }: { rules: ContentPricingRules; onRules: (r: ContentPricingRules) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [cat, setCat] = useState<VideoCategory>('solo');
  const [copyFrom, setCopyFrom] = useState<VideoCategory | ''>('');
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [src, setSrc] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const overrides = rules.overrides ?? [];
  const setOverrides = (rows: ContentPriceOverride[]) => onRules({ ...rules, overrides: rows });

  const preset = (k: PresetKey) => {
    if (!window.confirm(t('pricing.presetConfirm', { preset: t(`pricing.preset.${k}`) }))) return;
    onRules(presetPricingRules(k));
  };
  const openCopy = async () => {
    setCopyOpen(true);
    setMsg(null);
    if (accounts) return;
    try { setAccounts(await api.getAccounts()); } catch { setMsg(t('pricing.copyFailed')); }
  };
  const doCopy = async () => {
    const a = accounts?.find((x) => x.account_id === src);
    if (!a || busy) return;
    if (!window.confirm(t('pricing.copyConfirm', { account: a.profile_name || a.username || a.account_id }))) return;
    setBusy(true);
    try {
      const r = (await api.getContentPolicy(src)).pricing_rules;
      if (!matrixHasPricing(r)) { setMsg(t('pricing.copyEmpty')); return; }
      onRules(r);
      setCopyOpen(false);
      setSrc('');
      setMsg(t('pricing.copyDone'));
    } catch {
      setMsg(t('pricing.copyFailed'));
    } finally {
      setBusy(false);
    }
  };
  const copyInto = () => {
    if (!copyFrom || copyFrom === cat) return;
    if (!window.confirm(t('pricing.copyTagConfirm', { from: VIDEO_CATEGORY_LABEL[copyFrom], to: VIDEO_CATEGORY_LABEL[cat] }))) return;
    const cells: Record<string, PricePair> = {};
    for (const [b, p] of Object.entries(rules.video?.[copyFrom] ?? {})) cells[b] = [p?.[0] ?? null, p?.[1] ?? null];
    onRules({ ...rules, video: { ...(rules.video ?? {}), [cat]: cells } });
    setCopyFrom('');
  };
  const GRID = '64px repeat(11,minmax(0,1fr))';
  const OV_COLS = '118px 130px 80px 64px 72px 72px 26px';

  return (
    <Stack>
      <Card>
        <Row label={t('pricing.quick')} help={msg ?? t('pricing.quickHelp')} helpColor={msg ? C.purplePale : undefined}>
          {PRESET_KEYS.map((k) => (
            <button key={k} type="button" className="cpt-hover" onClick={() => preset(k)} style={{ height: 26, padding: '0 10px', border: `1px solid ${C.borderHi}`, borderRadius: 99, background: 'transparent', color: C.sub, font: font(500, 12), cursor: 'pointer' }}>{t(`pricing.preset.${k}`)}</button>
          ))}
          {!copyOpen ? (
            <Btn kind="muted" height={26} onClick={() => void openCopy()}>{t('pricing.copyAccount')}</Btn>
          ) : !accounts ? <Spin /> : (
            <>
              <Select value={src} onChange={setSrc} width={180} options={[{ id: '', label: t('pricing.copyPick') }, ...accounts.map((a) => ({ id: a.account_id, label: a.profile_name || a.username || a.account_id }))]} />
              <Btn kind="ghost" height={26} disabled={!src || busy} onClick={() => void doCopy()}>{t('pricing.copyApply')}</Btn>
              <Btn kind="text" height={26} onClick={() => { setCopyOpen(false); setSrc(''); }} style={{ color: C.sub }}>{t('cancel')}</Btn>
            </>
          )}
        </Row>
      </Card>

      <Section title={t('pricing.photoPrices')} hint={t('pricing.photoPricesHint')}>
        <Card>
          <THead cols={ITEM_COLS}><span>{t('pricing.tierCol')}</span><span>{t('pricing.default')}</span><span>{t('pricing.floor')}</span></THead>
          {PHOTO_TIERS.map((tier) => {
            const c = photoCell(rules, tier);
            return (
              <TRow key={tier} cols={ITEM_COLS}>
                <span style={{ font: font(500, 13), color: C.text }}>{PHOTO_TIER_LABEL[tier]}</span>
                <NumInput value={c[0] ?? null} onChange={(v) => onRules(setPhotoCell(rules, tier, 0, v))} min={0} max={9999} allowEmpty width="100%" />
                <NumInput value={c[1] ?? null} onChange={(v) => onRules(setPhotoCell(rules, tier, 1, v))} min={0} max={9999} allowEmpty width="100%" />
              </TRow>
            );
          })}
        </Card>
      </Section>

      <Section title={t('pricing.videoPrices')} hint={t('pricing.videoPricesHint')}>
        <Card>
          <div className="cpt-row" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', flexWrap: 'wrap' }}>
            <Seg value={cat} onChange={setCat} options={VIDEO_CATEGORIES.map((c) => ({
              id: c,
              label: <>{VIDEO_CATEGORY_LABEL[c]}<span style={{ width: 5, height: 5, borderRadius: 99, background: categoryHasPricing(rules, c) ? (c === cat ? C.purpleLt : C.faint) : 'transparent' }} /></>,
            }))} />
            <span style={{ flex: 1 }} />
            <Select value={copyFrom} onChange={setCopyFrom} width={150} options={[{ id: '' as VideoCategory | '', label: t('pricing.copyFrom') }, ...VIDEO_CATEGORIES.filter((c) => c !== cat).map((c) => ({ id: c as VideoCategory | '', label: VIDEO_CATEGORY_LABEL[c] }))]} />
            <Btn kind="muted" disabled={!copyFrom} onClick={copyInto}>{t('pricing.copyInto', { to: VIDEO_CATEGORY_LABEL[cat] })}</Btn>
          </div>
          <div className="cpt-scroll-x">
            <div style={{ minWidth: 720 }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 6, padding: '7px 14px', font: font(600, 10), letterSpacing: '.04em', textTransform: 'uppercase', color: C.muted, borderBottom: 0 }}>
                <span>{t('pricing.min')}</span>{MINUTE_BUCKETS.map((b) => <span key={b}>{b}</span>)}
              </div>
              {([0, 1] as const).map((idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 6, alignItems: 'center', padding: idx ? '0 14px 12px' : '0 14px 6px' }}>
                  <span style={{ font: font(500, 12), color: C.sub }}>{idx ? t('pricing.floor') : t('pricing.default')}</span>
                  {MINUTE_BUCKETS.map((b) => (
                    <NumInput key={b} value={videoCell(rules, cat, b)[idx] ?? null} onChange={(v) => onRules(setVideoCell(rules, cat, b, idx, v))} min={0} max={9999} allowEmpty width="100%" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Card>
      </Section>

      <Section title={t('pricing.overrides')} hint={t('pricing.overridesHint')}>
        <Card>
          {overrides.length > 0 && <THead cols={OV_COLS}><span>{t('pricing.media')}</span><span>{t('pricing.tierOrCat')}</span><span>{t('pricing.length')}</span><span>{t('pricing.count')}</span><span>{t('pricing.default')}</span><span>{t('pricing.floor')}</span><span /></THead>}
          {overrides.map((ov, i) => {
            const patch = (p: Partial<ContentPriceOverride>) => setOverrides(overrides.map((o, k) => (k === i ? { ...o, ...p } : o)));
            return (
              <TRow key={i} cols={OV_COLS}>
                <Seg value={ov.media} onChange={(m) => patch({ media: m })} options={[{ id: 'photo', label: t('pricing.photo') }, { id: 'video', label: t('pricing.video') }]} />
                {ov.media === 'photo'
                  ? <Select value={ov.tier || 'nude'} onChange={(v) => patch({ tier: v })} width={130} options={PHOTO_TIERS.map((x) => ({ id: x, label: PHOTO_TIER_LABEL[x] }))} />
                  : <Select value={ov.category || 'solo'} onChange={(v) => patch({ category: v })} width={130} options={VIDEO_CATEGORIES.map((x) => ({ id: x, label: VIDEO_CATEGORY_LABEL[x] }))} />}
                {ov.media === 'video'
                  ? <Select value={ov.bucket || ''} onChange={(v) => patch({ bucket: v || null })} width={80} options={[{ id: '', label: t('pricing.any') }, ...MINUTE_BUCKETS.map((b) => ({ id: b, label: b }))]} />
                  : <span />}
                <NumInput value={ov.count} onChange={(v) => v !== null && patch({ count: Math.max(1, v) })} min={1} max={50} int width="100%" />
                <NumInput value={ov.price?.[0] ?? null} onChange={(v) => patch({ price: [v, ov.price?.[1] ?? null] })} min={0} max={9999} allowEmpty width="100%" />
                <NumInput value={ov.price?.[1] ?? null} onChange={(v) => patch({ price: [ov.price?.[0] ?? null, v] })} min={0} max={9999} allowEmpty width="100%" />
                <XButton onClick={() => setOverrides(overrides.filter((_, k) => k !== i))} title={t('remove')} />
              </TRow>
            );
          })}
          {!overrides.length && <Empty>{t('pricing.noOverrides')}</Empty>}
          <Foot><Btn kind="dashed" height={26} onClick={() => setOverrides([...overrides, { media: 'photo', tier: 'nude', count: 3, price: [null, null] }])}>{t('pricing.addOverride')}</Btn></Foot>
        </Card>
      </Section>
    </Stack>
  );
}
